import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CircleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { request } from "../api";
import {
  EmptyState,
  ErrorState,
  EventRail,
  LoadingState,
  Metric,
  PageHeader,
  Panel,
  StatusPill,
} from "../components";
import { formatTime } from "../format";
import type { JournalEvent, Overview, Queue } from "../types";

export function OverviewPage() {
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: () => request<Overview>("/overview"),
    refetchInterval: 30_000,
  });
  const events = useQuery({
    queryKey: ["events", "overview"],
    queryFn: () => request<JournalEvent[]>("/data/events?limit=8"),
    refetchInterval: 15_000,
  });
  const queues = useQuery({
    queryKey: ["queues"],
    queryFn: () => request<Queue[]>("/system/queues"),
    refetchInterval: 15_000,
  });

  if (overview.isLoading) {
    return <LoadingState />;
  }
  if (overview.error || !overview.data) {
    return <ErrorState error={overview.error} />;
  }

  const data = overview.data;
  const latest = events.data?.[0];
  const failureRate = data.event_count > 0
    ? ((data.event_failures / data.event_count) * 100).toFixed(1)
    : "0.0";
  const backlog = queues.data?.reduce(
    (sum, queue) => sum + queue.pending + queue.retry + queue.archived,
    0,
  ) ?? 0;

  return (
    <>
      <PageHeader
        eyebrow="Engine state / last 24 hours"
        title="Every handoff, in one line of sight."
        description="The control room follows tenant data from the Lueira stream to its final provider outcome."
        actions={(
          <Link className="button button--secondary" to="/data">
            Open flow data <ArrowUpRight size={15} />
          </Link>
        )}
      />

      <section className="metric-strip" aria-label="Operational summary">
        <Metric label="Tenants observed" value={data.tenant_count} />
        <Metric label="Active connections" value={`${data.active_connections}/${data.connection_count}`} />
        <Metric label="Events received" value={data.event_count} />
        <Metric
          label="Failure rate"
          value={`${failureRate}%`}
          tone={data.event_failures > 0 ? "negative" : "positive"}
        />
        <Metric label="Queue backlog" value={backlog} tone={backlog > 25 ? "negative" : "default"} />
      </section>

      <div className="overview-grid">
        <Panel
          title="Latest event rail"
          meta={latest ? <StatusPill value={latest.outcome} /> : undefined}
          className="overview-grid__rail"
        >
          {latest ? (
            <>
              <div className="event-context">
                <div>
                  <small>Topic</small>
                  <strong>{latest.topic}</strong>
                </div>
                <div>
                  <small>Tenant</small>
                  <strong>{latest.tenant_id ?? "No tenant"}</strong>
                </div>
                <div>
                  <small>Received</small>
                  <strong>{formatTime(latest.received_at)}</strong>
                </div>
              </div>
              <EventRail stages={[
                { label: "Received", value: latest.stream_id, state: "done" },
                {
                  label: "Routed",
                  value: `${latest.matched_connection_count} connection${latest.matched_connection_count === 1 ? "" : "s"}`,
                  state: latest.matched_connection_count > 0 ? "done" : "idle",
                },
                {
                  label: "Queued",
                  value: latest.outcome === "ignored" ? "Not required" : "Asynq",
                  state: latest.outcome === "ignored" ? "idle" : "done",
                },
                {
                  label: "Provider",
                  value: latest.outcome,
                  state: ["failed", "dlq"].includes(latest.outcome)
                    ? "failed"
                    : latest.outcome === "delivered"
                      ? "done"
                      : "active",
                },
              ]} />
            </>
          ) : (
            <EmptyState
              title="No events have entered the journal"
              body="New Lueira stream events will appear here as soon as the worker receives them."
            />
          )}
        </Panel>

        <Panel title="Provider outcomes" meta={<span>24h</span>}>
          <div className="outcome-stack">
            <div>
              <span>Deliveries</span>
              <strong>{data.delivery_successes}</strong>
              <small>successful</small>
              <em>{data.delivery_failures} failed</em>
            </div>
            <div>
              <span>Operations</span>
              <strong>{data.operation_successes}</strong>
              <small>succeeded</small>
              <em>{data.operation_failures} failed</em>
            </div>
          </div>
        </Panel>

        <Panel title="Recent event activity" className="overview-grid__events">
          {events.isLoading && <LoadingState />}
          {events.error && <ErrorState error={events.error} />}
          {events.data && events.data.length > 0 ? (
            <div className="data-list">
              {events.data.map((event) => (
                <Link to={`/data?kind=events&selected=${event.id}`} className="data-list__row" key={event.id}>
                  <span className="data-list__marker" />
                  <div>
                    <strong>{event.topic}</strong>
                    <small>{event.tenant_id ?? "Unscoped"} · {formatTime(event.received_at)}</small>
                  </div>
                  <StatusPill value={event.outcome} />
                </Link>
              ))}
            </div>
          ) : !events.isLoading && (
            <EmptyState title="No recent activity" body="The worker has not journaled any events yet." />
          )}
        </Panel>

        <Panel title="Queue pressure" className="overview-grid__queues">
          {queues.data?.map((queue) => (
            <div className="queue-line" key={queue.name}>
              <div>
                <strong>{queue.name}</strong>
                <StatusPill value={queue.paused ? "paused" : "active"} />
              </div>
              <div className="queue-line__bar">
                <span
                  style={{ width: `${Math.min(100, (queue.size / Math.max(1, backlog)) * 100)}%` }}
                />
              </div>
              <small>{queue.pending} pending · {queue.retry} retry · {queue.archived} archived</small>
            </div>
          ))}
          {queues.data?.some((queue) => queue.archived > 0) && (
            <div className="inline-warning">
              <CircleAlert size={17} />
              Archived tasks need operator review before retry.
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}

